import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Collapse, 
  Container, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControlLabel, 
  Link, 
  Paper, 
  Switch, 
  Typography 
} from '@mui/material';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { applyConsent } from '../../utils/analytics';

interface CookieSettings {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const defaultCookieSettings: CookieSettings = {
  essential: true, // Always required
  analytics: false,
  marketing: false,
  functional: false,
};

const CookieConsent: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({ ...defaultCookieSettings });
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Function to validate if an object conforms to CookieSettings interface
  const isValidCookieSettings = (obj: unknown): obj is CookieSettings => {
    return obj !== null && 
      typeof obj === 'object' &&
      'essential' in obj && typeof (obj as CookieSettings).essential === 'boolean' &&
      'analytics' in obj && typeof (obj as CookieSettings).analytics === 'boolean' &&
      'marketing' in obj && typeof (obj as CookieSettings).marketing === 'boolean' &&
      'functional' in obj && typeof (obj as CookieSettings).functional === 'boolean';
  };

  // Check if consent has been given previously
  useEffect(() => {
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // If no consent has been given, show the banner
      setOpen(true);
    } else {
      // If consent has been given, load the settings
      try {
        const savedSettings = JSON.parse(consentGiven);
        if (isValidCookieSettings(savedSettings)) {
          setCookieSettings(savedSettings);
          applyConsent(savedSettings);
        } else {
          setCookieSettings(defaultCookieSettings);
        }
      } catch {
        // If parsing fails, use defaults
        setCookieSettings(defaultCookieSettings);
      }
    }
  }, []);

  // Expose the manageCookies function globally for footer link
  useEffect(() => {
    const manageCookies = () => {
      setSettingsDialogOpen(true);
    };
    (window as Window & { manageCookies?: () => void }).manageCookies = manageCookies;
    return () => {
      delete (window as Window & { manageCookies?: () => void }).manageCookies;
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookieSettings = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
      setCookieSettings(allAccepted);
    } catch {
      // Handle localStorage errors silently
    }
    applyConsent(allAccepted);

    setOpen(false);
    setSettingsDialogOpen(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly: CookieSettings = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
      setCookieSettings(essentialOnly);
    } catch {
      // Handle localStorage errors silently
    }
    applyConsent(essentialOnly);

    setOpen(false);
    setSettingsDialogOpen(false);
  };

  const handleSaveSettings = () => {
    const sanitizedSettings: CookieSettings = {
      essential: true, // Always true
      analytics: Boolean(cookieSettings.analytics),
      marketing: Boolean(cookieSettings.marketing),
      functional: Boolean(cookieSettings.functional),
    };
    
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(sanitizedSettings));
      setCookieSettings(sanitizedSettings);
    } catch {
      // Handle localStorage errors silently
    }
    applyConsent(sanitizedSettings);

    setSettingsDialogOpen(false);
    setOpen(false);
  };

  const handleToggleSetting = (settingName: keyof CookieSettings) => {
    if (settingName === 'essential') return; // Essential can't be toggled
    
    setCookieSettings((prev) => ({
      ...prev,
      [settingName]: !prev[settingName],
    }));
  };

  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
  };

  return (
    <>
      {/* Cookie Banner - only shown when open is true */}
      {open && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            padding: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
          }}
        >
          <Container>
            <Box display="flex" alignItems="flex-start" flexWrap="wrap">
              <PrivacyTipIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  Cookie Consent
                </Typography>
                <Typography variant="body2" gutterBottom>
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies.
                </Typography>
                <Collapse in={showSettings}>
                  <Box my={2}>
                    <Typography variant="body2" gutterBottom>
                      You can customize your cookie preferences by selecting which categories of cookies you allow us to use:
                    </Typography>
                  </Box>
                </Collapse>
                <Box mt={1}>
                  <Link 
                    component={RouterLink} 
                    to="/privacy-policy" 
                    sx={{ 
                      color: 'primary.main',
                      mr: 2,
                      fontSize: '0.875rem',
                    }}
                  >
                    Privacy Policy
                  </Link>
                  <Button
                    size="small"
                    onClick={() => setShowSettings(!showSettings)}
                    startIcon={<SettingsIcon />}
                    sx={{
                      color: 'primary.main',
                      textTransform: 'none',
                    }}
                  >
                    {showSettings ? 'Hide Preferences' : 'Cookie Preferences'}
                  </Button>
                </Box>
              </Box>
              <Box 
                display="flex" 
                flexDirection={{ xs: 'column', sm: 'row' }} 
                mt={{ xs: 2, sm: 0 }}
                ml={{ xs: 0, sm: 2 }}
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRejectNonEssential}
                  sx={{ 
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 1, sm: 0 },
                    minWidth: '120px',
                  }}
                >
                  Reject All
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAcceptAll}
                  sx={{ 
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 1, sm: 0 },
                    minWidth: '120px',
                  }}
                >
                  Accept All
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenSettings}
                  sx={{ minWidth: '120px' }}
                >
                  Settings
                </Button>
              </Box>
            </Box>
          </Container>
        </Paper>
      )}

      {/* Cookie Settings Dialog - always rendered so it can be opened from footer */}
      <Dialog
        open={settingsDialogOpen}
        onClose={handleCloseSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cookie Settings
          <Button 
            sx={{ 
              position: 'absolute', 
              right: 8, 
              top: 8,
              minWidth: 'auto',
            }}
            onClick={handleCloseSettings}
            aria-label="Close cookie settings"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Customize your cookie preferences below. Essential cookies are necessary for the website to function properly and cannot be disabled.
          </Typography>
          
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch 
                  checked={cookieSettings.essential} 
                  disabled 
                  aria-label="Essential cookies (required)"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Essential Cookies (Required)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These cookies are necessary for the website to function and cannot be switched off.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={cookieSettings.analytics} 
                  onChange={() => handleToggleSetting('analytics')}
                  aria-label="Analytics cookies"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Analytics Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These cookies allow us to count visits and traffic sources, so we can measure and improve the performance of our site.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={cookieSettings.functional} 
                  onChange={() => handleToggleSetting('functional')}
                  aria-label="Functional cookies"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Functional Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These cookies enable the website to provide enhanced functionality and personalization.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={cookieSettings.marketing} 
                  onChange={() => handleToggleSetting('marketing')}
                  aria-label="Marketing cookies"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Marketing Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These cookies may be set through our site by our advertising partners to build a profile of your interests.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRejectNonEssential} color="secondary">
            Reject All
          </Button>
          <Button onClick={handleAcceptAll} color="secondary">
            Accept All
          </Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
